import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Features } from '~/components/FeatureToggle';
import TabModal, { TabItem } from '~/components/Modal/TabModal';
import useConnectorModelPrefetch from '~/hooks/workflows/connector/useConnectorModelPrefetch';
import { useWorkflowTargetIds } from '~/hooks/workflows/useWorkflowTargetIds';
import {
    sessionDataAtom,
    sessionInitStatusAtom,
} from '~/state/atoms/sessionAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import AdaptConnectorWorkflow from './adapt/AdaptConnectorWorkflow';
import ContourConnectorWorkflow from './contour/ContourConnectorWorkflow';
import DataUpload from './data-upload';
import DosePredictionConnectorWorkflow from './dose/DosePredictionConnectorWorkflow';
import ImageTranslationConnectorWorkflow from './image/ImageTranslationConnectorWorkflow';

export default function ConnectorWorkflows() {
    const environmentObjectIds = useWorkflowTargetIds();
    const [workflowModal, setWorkflowModal] =
        useRecoilState(workflowModalState);

    const session = useRecoilValue(sessionDataAtom);
    const { isLoading, error } = useRecoilValue(sessionInitStatusAtom);

    // Prefetch model lists so they're cached before the dialog opens
    useConnectorModelPrefetch();

    const items = [
        AppConfig.isFeatureEnabled(Features.CONTOUR_WORKFLOW) && {
            name: <FormattedMessage id="contourPlus" />,
            description: <FormattedMessage id="contourPlusDescription" />,
            content: (
                <ContourConnectorWorkflow
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    isLoadingSession={isLoading}
                    error={error}
                />
            ),
            type: WorkflowTypes.CONTOUR,
        },
        AppConfig.isFeatureEnabled(Features.DOSE_WORKFLOW) && {
            name: <FormattedMessage id="dosePlus" />,
            description: <FormattedMessage id="dosePlusDescription" />,
            content: (
                <DosePredictionConnectorWorkflow
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    isLoadingSession={isLoading}
                    error={error}
                />
            ),
            type: WorkflowTypes.DOSE,
        },
        AppConfig.isFeatureEnabled(Features.IMAGE_TRANSLATION_WORKFLOW) && {
            name: <FormattedMessage id="imagePlus" />,
            description: <FormattedMessage id="imagePlusDescription" />,
            content: (
                <ImageTranslationConnectorWorkflow
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    isLoadingSession={isLoading}
                    error={error}
                />
            ),
            type: WorkflowTypes.IMAGE,
        },
        AppConfig.isFeatureEnabled(Features.ADAPT_WORKFLOW) && {
            name: <FormattedMessage id="adaptPlus" />,
            description: <FormattedMessage id="adaptPlusDescription" />,
            content: (
                <AdaptConnectorWorkflow
                    sessionData={session}
                    isLoadingSession={isLoading}
                    error={error}
                />
            ),
            type: WorkflowTypes.ADAPT,
        },
        AppConfig.isFeatureEnabled(Features.DATA_UPLOAD) && {
            name: <FormattedMessage id="shareData" />,
            description: <FormattedMessage id="shareDataDescription" />,
            content: (
                <DataUpload
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    showPseudonymization={false}
                />
            ),
            type: WorkflowTypes.DATA_UPLOAD,
        },
    ].filter(Boolean) as TabItem[];

    return (
        <TabModal
            maxWidth={800}
            open={workflowModal.open}
            setOpen={(open) => setWorkflowModal({ ...workflowModal, open })}
            items={items}
            selectedPage={workflowModal.page}
            setSelectedPage={(page) =>
                setWorkflowModal({
                    ...workflowModal,
                    page: page as WorkflowTypes,
                })
            }
        />
    );
}
